import type { App } from 'vue';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import VueAxios from 'vue-axios';
import JwtService from '@/admin/core/services/JwtService';
import { inertiaVersionCheck } from '@/utils/inertia';
import { axiosXSRFTokenInterceptor } from '@/utils/csrfToken';

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
    /**
     * @description property to share vue instance
     */
    public static vueInstance: App;

    /**
     * @description initialize vue axios
     */
    public static init(app: App<Element>) {
        ApiService.vueInstance = app;
        ApiService.vueInstance.use(VueAxios, axios);
        ApiService.vueInstance.axios.defaults.baseURL = import.meta.env.VITE_APP_API_URL;

        // request interceptor 설정 - XSRF-TOKEN 확인
        ApiService.vueInstance.axios.interceptors.request.use(axiosXSRFTokenInterceptor, (error) => {
            return Promise.reject(error);
        });

        // response interceptor 설정
        ApiService.vueInstance.axios.interceptors.response.use(
            (response) => {
                inertiaVersionCheck(response);

                return Promise.resolve(response);
            },
            (error) => {
                if (axios.isCancel(error)) {
                    return new Promise(() => {
                        //
                    });
                }

                // 에러 응답이 없는 경우 (네트워크 오류 등)
                if (!error.response) {
                    return Promise.reject(error);
                }

                const { config, data } = error.response;

                // Inertia 요청인지 확인
                const isInertiaRequest = config.headers['X-Inertia'] === 'true';

                // Inertia 요청이면 에러를 그대로 전달하여 Inertia에서 처리하도록 함
                if (isInertiaRequest) {
                    return Promise.reject(error);
                }

                // 일반 API 요청의 경우 data를 reject
                return Promise.reject(data);
            },
        );
    }

    /**
     * @description set the default HTTP request headers
     */
    public static setHeader(): void {
        ApiService.vueInstance.axios.defaults.headers.common['Authorization'] = `Token ${JwtService.getToken()}`;
        ApiService.vueInstance.axios.defaults.headers.common['Accept'] = 'application/json';
    }

    /**
     * @description send the GET HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static query(resource: string, params: any): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.get(resource, params);
    }

    /**
     * @description send the GET HTTP request
     * @param resource: string
     * @param slug: string
     * @returns Promise<AxiosResponse>
     */
    public static get<T = any>(resource: string): Promise<AxiosResponse<T>> {
        return ApiService.vueInstance.axios.get<T>(`${resource}`);
    }

    /**
     * @description set the POST HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static post<T = any>(resource: string, params: any): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.post<T>(`${resource}`, params);
    }

    /**
     * @description send the UPDATE HTTP request
     * @param resource: string
     * @param slug: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static update<T = any>(resource: string, slug: string, params: any): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.put<T>(`${resource}/${slug}`, params);
    }

    /**
     * @description Send the PUT HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static put<T = any>(resource: string, params: any): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.put<T>(`${resource}`, params);
    }

    /**
     * @description Send the DELETE HTTP request
     * @param resource: string
     * @returns Promise<AxiosResponse>
     */
    public static delete<T = any>(resource: string): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.delete<T>(resource);
    }
}

export default ApiService;
